import React, { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import PropTypes from 'prop-types';
import { map } from 'lodash';

import {
  hasPublisherRole,
  hasOrgPublisherRole,
} from '../../../../utils';
import {
  RadioGroupSection,
  FormActionButtons,
  ErrorContainer,
  SearchTextListContainer,
} from '../../../../components';
import {
  ID_PUBLIC,
  ID_PRIVATE,
  ID_RESTRICTED,
  API_ACCESS_STATUS_PUBLIC,
  VISIBILTY_PERMISSIONS_DATA,
  VISIBILITY_ORG_TITLE,
  VISIBILITY_ORG_SUB_TITLE,
  VISIBILITY_ORG_PLACE_HOLDER_TEXT,
  VISIBILITY_SELECTED_ORG_TITLE,
  VISIBILITY_SELECTED_ORG_SUB_TITLE,
  VISIBILITY_SELECTED_ORG_PLACE_HOLDER_TEXT,
  SAVE_AND_NEXT_TEXT,
  VISIBILITY_ASSIST_TEXT,
  VISIBILITY_SELECTED_ITEM_ERROR_MESSAGE,
  API_ACCESS_STATUS_PRIVATE,
} from '../labels';

const VISIBILITY_ORGADMIN_DEV_DATA = VISIBILTY_PERMISSIONS_DATA.filter(
  option => option.id !== ID_RESTRICTED);

export default function VisibilityPermission(props) {
  const {
    classes,
    userContext = {},
    apiUuid,
    tabValue,
    apiDetails,
    organizations,
    apiAccessStatus,
    onLoadMore,
    isLoadMore,
    onCancel,
    errors,
   } = props;

  const isDisabled = !(apiUuid);

  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const getDefaultPermission = () => {
    let defaultPermission = ID_PRIVATE;
    if (apiDetails.accessStatus === API_ACCESS_STATUS_PUBLIC) {
      defaultPermission = ID_PUBLIC;
    } else if (props.selectedOrganizations.length === 1 &&
      props.selectedOrganizations[0].uuid === apiDetails.managingOrgUuid) {
      defaultPermission = ID_PRIVATE;
    } else if (props.selectedOrganizations.length > 0) {
      defaultPermission = ID_RESTRICTED;
    }
    return defaultPermission;
  };
  const [selectedPermission, setPermission] = useState(getDefaultPermission());
  const [selectedOrganizations, setSelectedOrganizations] = useState(props.selectedOrganizations);
  const data = userContext.portalAdmin || userContext.apiOwner || userContext.orgPublisher ?
    VISIBILTY_PERMISSIONS_DATA : VISIBILITY_ORGADMIN_DEV_DATA;
  const isDropDownOrg = false;
  const disableSelectedOptions = apiDetails ? [{ uuid: apiDetails.managingOrgUuid }] : [];

  const fetchVisibilityData = async () => {
    await props.fetchSelectedOrganizations(apiUuid);
  };

  useEffect(() => {
    if ((hasPublisherRole(userContext) || hasOrgPublisherRole(userContext))
      && apiUuid && tabValue === 'visibility-tab') {
      fetchVisibilityData();
    }
  }, [tabValue]);

  useEffect(() => {
    setSelectedOrganizations(props.selectedOrganizations);
  }, [props.selectedOrganizations]);

  useEffect(() => {
    setPermission(getDefaultPermission());
  }, [apiAccessStatus, props.selectedOrganizations, apiDetails]);

  const onSearchListChange = (selectedOrg) => {
    setSelectedOrganizations([...selectedOrg]);
    setIsUnsavedChanges(true);
    localStorage.setItem('isUnSavedChanges', true);
  };

  const onChangePermission = (e) => {
    setPermission(e.target.value);
    setIsUnsavedChanges(true);
    localStorage.setItem('isUnSavedChanges', true);
  };

  const onNextVisibility = () => {
    const accessStatus = selectedPermission === ID_PUBLIC ?
    API_ACCESS_STATUS_PUBLIC : API_ACCESS_STATUS_PRIVATE;
    const canSaveOrgadminDev = userContext.orgAdmin
    || userContext.developer;
    const dataToSave = [];
    if (selectedPermission === ID_RESTRICTED || selectedPermission === ID_PRIVATE) {
      map(selectedOrganizations, object => {
        dataToSave.push(object.uuid);
      });
    }
    props.saveApiAccessStatus(accessStatus, apiUuid, canSaveOrgadminDev, dataToSave);
  };

  const onNext = () => {
    if (selectedPermission === ID_RESTRICTED && selectedOrganizations.length === 0) {
      return false;
    }
    setIsUnsavedChanges(false);
    localStorage.setItem('isUnSavedChanges', false);
    onNextVisibility();
    return true;
  };

  return (
    <div id="api-visibility-container">
      <div className={classes.pageTitle}>API Visibility for Organizations</div>
      <RadioGroupSection
        selected={selectedPermission}
        onChange={onChangePermission}
        data={data}
        id="visibility-radio-group"
        isDropDown={isDropDownOrg}
      />
      {errors.length > 0 &&
        <ErrorContainer errors={errors} />
      }
      {selectedPermission === ID_RESTRICTED &&
        <Grid container spacing={3} className={classes.gridContainer}>
          <Grid md={6} item>
            <SearchTextListContainer
              title={VISIBILITY_ORG_TITLE}
              subtitle={VISIBILITY_ORG_SUB_TITLE}
              placeHolderText={VISIBILITY_ORG_PLACE_HOLDER_TEXT}
              defaultList={organizations}
              defaultSelectedList={selectedOrganizations}
              listId={'organizations-list'}
              isCheckBoxVisible
              isLoadMore={isLoadMore}
              onLoadMore={onLoadMore}
              onSearchListChange={onSearchListChange}
              userContext={userContext}
              isDisableSelectedOption
              disableSelectedOptions={disableSelectedOptions}
            />
          </Grid>
          <Grid md={6} item>
            <SearchTextListContainer
              title={VISIBILITY_SELECTED_ORG_TITLE}
              subtitle={VISIBILITY_SELECTED_ORG_SUB_TITLE}
              isSelectedNumberVisible
              placeHolderText={VISIBILITY_SELECTED_ORG_PLACE_HOLDER_TEXT}
              defaultList={selectedOrganizations}
              defaultSelectedList={selectedOrganizations}
              listId={'organizations-selected-list'}
              onSearchListChange={onSearchListChange}
              isDeleteIconVisible
              userContext={userContext}
              selectedItemErrorMessage={VISIBILITY_SELECTED_ITEM_ERROR_MESSAGE}
              isDisableSelectedOption
              disableSelectedOptions={disableSelectedOptions}
            />
          </Grid>
        </Grid>
      }
      <FormActionButtons
        onNextClick={onNext}
        assistText={VISIBILITY_ASSIST_TEXT}
        nextText={SAVE_AND_NEXT_TEXT}
        onCancelClick={() => onCancel(isUnsavedChanges)}
        id="visibility-form-buttons"
        isDisabled={isDisabled}
      />
    </div>
  );
}

VisibilityPermission.propTypes = {
  classes: PropTypes.shape({}),
  userContext: PropTypes.shape({}),
  apiDetails: PropTypes.shape({}),
  selectedOrganizations: PropTypes.arrayOf(PropTypes.shape({})),
  organizations: PropTypes.arrayOf(PropTypes.shape({})),
  apiAccessStatus: PropTypes.string,
  onLoadMore: PropTypes.func,
  isLoadMore: PropTypes.bool,
  onCancel: PropTypes.func,
  errors: PropTypes.arrayOf(PropTypes.shape({})),
  apiUuid: PropTypes.string,
  tabValue: PropTypes.string,
};



// WEBPACK FOOTER //
// ./src/pages/Api/Edit/VisibilityPermissions/index.js